import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { memo, useEffect, useState } from 'react';
import { IGetNewPersonStats } from '~/utils/interface/dashboard';

type NewProvinceStatsProps = {
  data?: Array<IGetNewPersonStats>;
};

const NewProvinceStats = (props: NewProvinceStatsProps) => {
  const { t } = useTranslation('admin/home');

  const { data } = props;

  const safeData = Array.isArray(data) ? data.filter((x) => x.id !== 0) : [];
  const computedMax = Math.max(...safeData.map((item: any) => Number(item.total) || 0), 0);
  const yMax = Math.ceil(computedMax * 1.1);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 1699);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1699);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  function CustomXAxisTick({ x, y, payload, t }: any) {
    const monthLabel = t('total');
    const value = payload.value;

    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={10} textAnchor='middle' fill='#6b7280' fontSize={12}>
          {isMobile ? (
            <>
              <tspan x='0' dy='16'>
                {monthLabel}
              </tspan>
              <tspan x='0' dy='1.2em'>
                {value}
              </tspan>
            </>
          ) : (
            <tspan x='0' dy='16'>{`${value}`}</tspan>
          )}
        </text>
      </g>
    );
  }

  function CustomTooltip({ active, payload, label, t }: any) {
    if (!active || !payload?.length) return null;

    return (
      <Box className='rounded-2xl border bg-white px-3 py-2 shadow-lg'>
        <Typography className='text-sm font-semibold text-[#123B6D]'>{label}</Typography>
        <Typography className='text-sm text-slate-600'>
          {t('total')}
          <Typography component={'span'} className='font-semibold text-[#123B6D]'>
            {payload[0].value}
          </Typography>
        </Typography>
      </Box>
    );
  }

  return (
    <Box className='bg-white'>
      <ResponsiveContainer width='100%' height={400}>
        <BarChart data={safeData} margin={{ top: 30, right: 20, left: 10 }}>
          <CartesianGrid strokeDasharray='3 3' vertical={false} />
          <XAxis
            dataKey='name'
            tick={<CustomXAxisTick t={t} />}
            axisLine={false}
            tickLine={false}
            height={64}
            dy={15}
          />
          <YAxis domain={[0, yMax]} axisLine={false} tickLine={false} allowDecimals={false} width={30} />
          <Tooltip content={<CustomTooltip t={t} />} />
          <Bar dataKey='total' radius={[6, 6, 6, 6]} maxBarSize={26}>
            <LabelList
              dataKey='total'
              position='top'
              offset={2}
              style={{ fill: '#374151', fontSize: 12, fontWeight: 500 }}
            />
            {safeData.map((_, i) => (
              <Cell key={`cell-${i}`} fill='#509EE8' />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default memo(NewProvinceStats);
